/**
 * GT-004 / GT-010 — Google Consent Mode v2 (TSD: `app/utils/gtm-consent.js`).
 */

const toTrimmedString = (value) => (typeof value === 'string' ? value.trim() : '');

const GTM_CONSENT_REQUIRED_SITE_IDS = new Set(['kl-us', 'kl-uk']);
const CMP_CONSENT_STORAGE_PREFIX = 'kl_gtm_cmp_consent';

const CONSENT_DEFAULT_GRANTED = {
  ad_personalization: 'granted',
  ad_storage: 'granted',
  ad_user_data: 'granted',
  analytics_storage: 'granted',
};

const CONSENT_DEFAULT_DENIED = {
  ad_personalization: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  analytics_storage: 'denied',
  functionality_storage: 'granted',
  security_storage: 'granted',
  wait_for_update: 500,
};

const getCmpConsentStorageKey = (siteId) => {
  const id = toTrimmedString(siteId).toLowerCase();

  return id ? `${CMP_CONSENT_STORAGE_PREFIX}_${id}` : CMP_CONSENT_STORAGE_PREFIX;
};

const toConsentUpdatePayload = (analytics, marketing) => {
  const marketingStatus = marketing ? 'granted' : 'denied';

  return {
    ad_personalization: marketingStatus,
    ad_storage: marketingStatus,
    ad_user_data: marketingStatus,
    analytics_storage: analytics ? 'granted' : 'denied',
  };
};

const ensureGtag = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };
};

/**
 * @param {{ id?: string, preferences?: object }|null|undefined} site
 * @returns {boolean}
 */
export const resolveGtmDefaultConsentGranted = (site) => {
  const pref = site?.preferences?.gtmConsentGranted;

  if (pref === true || pref === 'true') {
    return true;
  }

  if (pref === false || pref === 'false') {
    return false;
  }

  const siteId = String(site?.id || '').trim().toLowerCase();

  return siteId.length > 0 && !GTM_CONSENT_REQUIRED_SITE_IDS.has(siteId);
};

/**
 * @param {boolean} [defaultConsentGranted=true]
 * @returns {string}
 */
export const buildConsentDefaultScript = (defaultConsentGranted = true) => {
  const consent = defaultConsentGranted ? CONSENT_DEFAULT_GRANTED : CONSENT_DEFAULT_DENIED;

  return `
window.dataLayer = window.dataLayer || [];
window.gtag = window.gtag || function(){window.dataLayer.push(arguments);};
if (!window.__klGtmConsentDefaultSet) {
  window.__klGtmConsentDefaultSet = true;
  gtag('consent', 'default', ${JSON.stringify(consent)});
}
`.trim();
};

/**
 * @param {{ analytics: boolean, marketing: boolean }} consent
 * @param {string} [siteId]
 */
export const persistCmpConsentChoice = (consent, siteId) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(getCmpConsentStorageKey(siteId), JSON.stringify(consent));
  } catch {
    // ignore quota / private mode
  }
};

/**
 * @param {{ analytics: boolean, marketing: boolean }} consent
 * @param {string} [siteId]
 * @param {boolean} [gtmEnabled=false]
 * @returns {boolean}
 */
export const applyCmpConsentChoice = (consent, siteId, gtmEnabled = false) => {
  persistCmpConsentChoice(consent, siteId);

  return gtmEnabled ? updateGTMConsent(consent) : false;
};

/**
 * @param {{ analytics: boolean, marketing: boolean }} consent
 * @returns {boolean}
 */
export const updateGTMConsent = (consent) => {
  if (typeof window === 'undefined') {
    return false;
  }

  const {analytics, marketing} = consent ?? {};

  if (typeof analytics !== 'boolean' || typeof marketing !== 'boolean') {
    return false;
  }

  const payload = toConsentUpdatePayload(analytics, marketing);
  const consentKey = JSON.stringify(payload);

  if (window.__klGtmConsentUpdate === consentKey) {
    return true;
  }

  ensureGtag();
  window.gtag('consent', 'update', payload);
  window.__klGtmConsentUpdate = consentKey;

  return true;
};

/**
 * @returns {IArguments|undefined|null}
 */
export const getConsentState = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.dataLayer?.find((item) => item?.[0] === 'consent') ?? null;
};

/**
 * @param {boolean|undefined} selectedDnt
 * @param {boolean} [gtmEnabled=false]
 * @param {string} [siteId]
 * @returns {boolean}
 */
export const syncGtmConsentFromDnt = (selectedDnt, gtmEnabled = false, siteId) => {
  if (!gtmEnabled || typeof selectedDnt !== 'boolean') {
    return false;
  }

  try {
    const raw = window.localStorage?.getItem(getCmpConsentStorageKey(siteId));

    if (raw) {
      const parsed = JSON.parse(raw);

      if (typeof parsed?.analytics === 'boolean' && typeof parsed?.marketing === 'boolean') {
        return updateGTMConsent(parsed);
      }
    }
  } catch {
    // ignore invalid storage
  }

  const granted = selectedDnt === false;

  return updateGTMConsent({analytics: granted, marketing: granted});
};
